import {
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { ListboxChangeEvent, Listbox } from '@openng/optimus-ui/listbox';
import { take } from 'rxjs/operators';
import { BaseContainer } from 'src/app/core/containers/base-container';
import {
  dynamicSort,
  isNullOrEmptyArray,
} from 'src/app/core/functions/common-functions';
import { EmailGroup } from 'src/app/model/admin/email-group';
import { EmailGroupMember } from 'src/app/model/admin/email-group-member';
import { Contact } from 'src/app/model/contacts/contact';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { InputText } from '@openng/optimus-ui/inputtext';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Textarea } from '@openng/optimus-ui/textarea';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';

@Component({
  selector: 'app-email-dialog-container',
  templateUrl: './email-dialog-container.component.html',
  styleUrls: ['./email-dialog-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AutoComplete,
    InputText,
    ButtonDirective,
    Textarea,
    Listbox,
    TranslatePipe,
  ],
})
export class EmailDialogContainerComponent extends BaseContainer {
  private _fb = inject(UntypedFormBuilder);
  private _adminApi = inject(AdminApiService);
  private _contactApi = inject(ContactApiService);

  /**
   * Flag to indicate if dialog should show or not.
   */
  readonly sendLabel = input('COMMON.LABEL.BUTTONS.SEND');

  config = inject(DynamicDialogConfig);
  ref = inject(DynamicDialogRef);

  emailGroups: EmailGroup[] = [];
  contacts: SelectItem[] = [];
  emailDialogForm!: UntypedFormGroup;
  lsEmailList!: UntypedFormGroup;
  emailList: any[] = [];
  emailNotes: any;
  selectedItem: any;
  contactPurpose: any;

  constructor() {
    super();
    this.emailDialogForm = this._fb.group({
      groups: [null],
      contacts: [null],
      emailAddresses: [null],
      lsEmailList: [null],
      emailNotes: [null],
    });
  }

  emailGroupSearch($event: AutoCompleteCompleteEvent): void {
    const query = $event && $event.query ? $event.query.toLowerCase() : '';
    const sort: any = {},
      apiQuery: any = {};
    sort['name'] = 'ASC';
    apiQuery['name'] = query;
    apiQuery['enabled'] = 'true';
    apiQuery['businessUnits'] = null;
    this._adminApi
      .queryForEmailGroups(null, 1, apiQuery, sort)
      .subscribe((emailGroups) => {
        this.emailGroups = emailGroups.sort(dynamicSort('name', 1));
      });
  }

  contactSearch($event: AutoCompleteCompleteEvent): void {
    const query = {
      firstName: $event.query.toLowerCase(),
    };
    const sort = {
      firstName: 'ASC',
    };
    this._contactApi
      .queryForContactDropdown(999999, 1, query, sort)
      .pipe(take(1))
      .subscribe((data) => {
        this.contacts = data.items.map(
          (c) =>
            ({
              fullName: c.firstName + ' ' + c.lastName,
              id: c.id,
            }) as any
        );
      });
  }

  groupSelected($event: AutoCompleteSelectEvent): void {
    const interior = $event.value;
    if (!isNullOrUndefined(interior)) {
      const listItem = {
        name: interior.name,
        members: interior.EmailGroupMembers,
      };
      this.emailList = [...this.emailList, listItem];
    }
    this.emailDialogForm.controls['groups'].patchValue(null, {
      emitEvent: true,
    });
  }

  contactSelected($event: AutoCompleteSelectEvent): void {
    const interior = $event.value;
    if (!isNullOrUndefined(interior)) {
      this._contactApi.getContact(interior.id).subscribe((contact) => {
        const listItem = {
          name: interior.fullName,
          address: contact?.adAccountName,
        };
        this.emailList = [...this.emailList, listItem];
        this.emailDialogForm.controls['contacts'].patchValue(null, {
          emitEvent: true,
        });
      });
    }
  }

  addEmailToList(): void {
    const ffEmail = this.emailDialogForm.controls['emailAddresses'].value;
    if (!isNullOrUndefined(ffEmail)) {
      const listItem = {
        name: ffEmail,
      };
      this.emailList = [...this.emailList, listItem];
    }
    this.emailDialogForm.controls['emailAddresses'].patchValue(null, {
      emitEvent: true,
    });
  }

  showEmailList(): boolean {
    return !isNullOrEmptyArray(this.emailList);
  }

  itemSelected($event: ListboxChangeEvent): void {
    if (!isNullOrUndefined($event)) {
      this.contactPurpose = 'TO';
      this.selectedItem = $event.value;
    }
  }

  removeListItem(): void {
    if (!isNullOrUndefined(this.contactPurpose)) {
      const sContactPurpose = this.contactPurpose;

      if (sContactPurpose === 'TO') {
        const sSelectedValue =
          this.emailDialogForm.controls['lsEmailList'].value;
        const index = this.emailList.findIndex(
          (x) => x.name === sSelectedValue.name
        );
        if (index !== -1) {
          this.emailList.splice(index, 1);
          this.emailList = [...this.emailList];
        }
      }
    }
  }

  sendEmail(): void {
    let toList: string | null = null;
    let sEmailNotes: string;

    if (!isNullOrEmptyArray(this.emailList)) {
      //building toList
      this.emailList.forEach((item) => {
        if (!isNullOrUndefined(item.members)) {
          item.members.forEach((member: EmailGroupMember) => {
            toList = this.addToList(toList, member.address);
          });
        } else if (!isNullOrUndefined(item.address)) {
          toList = this.addToList(toList, item.address);
        } else {
          toList = this.addToList(toList, item.name);
        }
      });

      sEmailNotes = this.emailDialogForm.controls['emailNotes'].value;
    } else {
      toList = null;
      sEmailNotes = '';
    }

    this.ref.close({ toList, bccList: '', sEmailNotes });
  }

  private addToList(toList: string | null, item: string) {
    if (isNullOrUndefined(toList)) {
      toList = item;
    } else {
      toList += ',' + item;
    }
    return toList;
  }
}
